import { useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, App as AntdApp, Button, Card, DatePicker, Flex, Form, Input, Modal, Popconfirm, Select, Space, Switch, Table, Tag, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { DeleteOutlined, EditOutlined, KeyOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons'
import dayjs, { type Dayjs } from 'dayjs'
import type { ApiKeyRecord, ApiKeyScope, CreateApiKeyPayload, UpdateApiKeyPayload } from '../../shared/services/api'
import { getApiService } from '../../shared/services/api'
import { dateTimeToAppTimezone, formatInAppTimezone } from '../../shared/utils/timezone'
import { PageHeader } from '../../shared/ui/PageHeader'

type ApiKeyFormValues = {
  name: string
  scopes: ApiKeyScope[]
  allowed_ips?: string
  metadata?: string
  expires_at?: Dayjs | null
  is_active?: boolean
}

const apiKeyScopeOptions: Array<{ value: ApiKeyScope; label: string }> = [
  { value: 'emails:send', label: 'Отправка писем' },
  { value: 'emails:status', label: 'Просмотр состояния писем' },
]

export function ApiKeysPage() {
  const { message } = AntdApp.useApp()
  const api = useMemo(() => getApiService(), [])
  const [form] = Form.useForm<ApiKeyFormValues>()
  const [keys, setKeys] = useState<ApiKeyRecord[]>([])
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [editing, setEditing] = useState<ApiKeyRecord | null>(null)
  const [editorOpen, setEditorOpen] = useState(false)
  const [generatedKey, setGeneratedKey] = useState<string | null>(null)
  const [deletingId, setDeletingId] = useState<number | null>(null)
  const load = useCallback(async () => {
    setLoading(true)
    try {
      const response = await api.listApiKeys()
      setKeys(response.data.keys)
    } catch (_err) {
      message.error('Не удалось загрузить API ключи')
    } finally {
      setLoading(false)
    }
  }, [api])

  useEffect(() => {
    void load()
  }, [load])

  const openCreate = () => {
    setEditing(null)
    form.setFieldsValue({
      name: '',
      scopes: ['emails:send', 'emails:status'],
      allowed_ips: '',
      metadata: '{}',
      expires_at: null,
      is_active: true,
    })
    setEditorOpen(true)
  }

  const openEdit = (key: ApiKeyRecord) => {
    setEditing(key)
    form.setFieldsValue({
      name: key.name,
      scopes: key.scopes,
      allowed_ips: key.allowedIps.join('\n'),
      metadata: JSON.stringify(key.metadata ?? {}, null, 2),
      expires_at: key.expiresAt ? dateTimeToAppTimezone(key.expiresAt) : null,
      is_active: key.isActive,
    })
    setEditorOpen(true)
  }

  const submit = async () => {
    const values = await form.validateFields()
    const metadata = parseMetadata(values.metadata)
    if (!metadata.ok) {
      message.error('Дополнительные данные должны быть объектом в формате JSON')
      return
    }

    const payload: CreateApiKeyPayload | UpdateApiKeyPayload = {
      name: values.name.trim(),
      scopes: values.scopes,
      allowed_ips: parseList(values.allowed_ips),
      metadata: metadata.value,
      expires_at: normalizeDate(values.expires_at),
    }

    setSubmitting(true)
    try {
      if (editing) {
        await api.updateApiKey(editing.id, {
          ...payload,
          is_active: Boolean(values.is_active),
        })
        message.success('API ключ обновлен')
      } else {
        const response = await api.createApiKey(payload as CreateApiKeyPayload)
        setGeneratedKey(response.data.api_key)
        message.success('API ключ создан')
      }
      setEditorOpen(false)
      await load()
    } catch (_err) {
      message.error(editing ? 'Не удалось обновить API ключ' : 'Не удалось создать API ключ')
    } finally {
      setSubmitting(false)
    }
  }

  const deleteKey = async (key: ApiKeyRecord) => {
    setDeletingId(key.id)
    try {
      await api.deleteApiKey(key.id)
      message.success('API ключ удален')
      await load()
    } catch (_err) {
      message.error('Не удалось удалить API ключ')
    } finally {
      setDeletingId(null)
    }
  }

  const columns: ColumnsType<ApiKeyRecord> = [
    {
      title: 'Название',
      dataIndex: 'name',
      width: 220,
      render: (value, key) => (
        <Flex vertical gap={2}>
          <Typography.Text strong>{value}</Typography.Text>
          <Typography.Text type="secondary">ID {key.id}</Typography.Text>
        </Flex>
      ),
    },
    {
      title: 'Права доступа',
      dataIndex: 'scopes',
      render: (scopes: ApiKeyScope[]) => (
        <Space size={[4, 4]} wrap>
          {scopes.map((scope) => <Tag key={scope}>{scope}</Tag>)}
        </Space>
      ),
    },
    {
      title: 'Разрешённые сетевые адреса',
      dataIndex: 'allowedIps',
      width: 220,
      render: (ips: string[]) => ips.length ? (
        <Flex vertical gap={2}>{ips.map((ip) => <Typography.Text key={ip} code>{ip}</Typography.Text>)}</Flex>
      ) : <Tag color="warning">Без ограничений</Tag>,
    },
    {
      title: 'Статус',
      dataIndex: 'isActive',
      width: 130,
      render: (active: boolean) => active ? <Tag color="success">Активен</Tag> : <Tag>Отключен</Tag>,
    },
    {
      title: 'Обновлен',
      dataIndex: 'updatedAt',
      width: 170,
      render: (value: string) => formatDate(value),
    },
    {
      title: '',
      key: 'actions',
      width: 112,
      align: 'right',
      render: (_, key) => (
        <Space.Compact>
          <Button
            type="text"
            icon={<EditOutlined />}
            aria-label={`Редактировать ${key.name}`}
            onClick={() => openEdit(key)}
          />
          <Popconfirm
            title="Архивировать API ключ?"
            description={key.name}
            okText="Архивировать"
            cancelText="Отмена"
            okButtonProps={{ danger: true }}
            onConfirm={() => deleteKey(key)}
          >
            <Button
              danger
              type="text"
              icon={<DeleteOutlined />}
              aria-label={`Удалить ${key.name}`}
              loading={deletingId === key.id}
            />
          </Popconfirm>
        </Space.Compact>
      ),
    },
  ]

  return (
    <Flex className="api-keys-page" vertical gap="middle">
      <PageHeader
        title="API ключи"
        description="Доступ приложений: права, сетевые ограничения и срок действия. Баланс находится в разделе «Счета»."
        icon={<KeyOutlined />}
        extra={(
          <Space size="small" wrap>
            <Button icon={<ReloadOutlined />} loading={loading} onClick={() => void load()}>Обновить</Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>Новый ключ</Button>
          </Space>
        )}
      />

      <Card className="table-card">
        <Table<ApiKeyRecord>
          rowKey="id"
          columns={columns}
          dataSource={keys}
          loading={loading}
          pagination={false}
          scroll={{ x: 1050 }}
        />
      </Card>

      <Modal
        open={editorOpen}
        title={editing ? 'Редактирование API ключа' : 'Новый API ключ'}
        okText={editing ? 'Сохранить' : 'Создать'}
        cancelText="Отмена"
        confirmLoading={submitting}
        width={720}
        onCancel={() => setEditorOpen(false)}
        onOk={() => void submit()}
        destroyOnHidden
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="Название" rules={[{ required: true, message: 'Укажите название' }]}>
            <Input maxLength={255} />
          </Form.Item>
          <Form.Item name="scopes" label="Права доступа" rules={[{ required: true, message: 'Выберите хотя бы одно право' }]}>
            <Select mode="multiple" options={apiKeyScopeOptions} />
          </Form.Item>
          <Form.Item name="allowed_ips" label="Разрешённые сетевые адреса">
            <Input.TextArea rows={3} placeholder="Один адрес на строку. Пусто — без ограничений." />
          </Form.Item>
          <Form.Item name="metadata" label="Дополнительные данные">
            <Input.TextArea rows={5} spellCheck={false} />
          </Form.Item>
          <Form.Item name="expires_at" label="Истекает">
            <DatePicker showTime format="YYYY-MM-DD HH:mm:ss" style={{ width: '100%' }} placeholder="Без срока действия" />
          </Form.Item>
          {editing && (
            <Form.Item name="is_active" label="Активен" valuePropName="checked">
              <Switch />
            </Form.Item>
          )}
        </Form>
      </Modal>

      <Modal
        open={Boolean(generatedKey)}
        title="API ключ создан"
        okText="Закрыть"
        cancelButtonProps={{ style: { display: 'none' } }}
        onOk={() => setGeneratedKey(null)}
        onCancel={() => setGeneratedKey(null)}
      >
        <Flex vertical gap="middle">
          <Alert type="warning" showIcon message="Ключ показывается только один раз. В базе хранится только его необратимая контрольная сумма." />
          <Typography.Text copyable code className="api-key-generated-value">{generatedKey}</Typography.Text>
        </Flex>
      </Modal>
    </Flex>
  )
}

function parseList(value?: string): string[] {
  return (value ?? '')
    .split(/[\n,]+/)
    .map((item) => item.trim())
    .filter(Boolean)
}

function parseMetadata(value?: string): { ok: true; value: Record<string, unknown> } | { ok: false } {
  const raw = value?.trim()
  if (!raw) return { ok: true, value: {} }

  try {
    const parsed = JSON.parse(raw) as unknown
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return { ok: true, value: parsed as Record<string, unknown> }
    }
    return { ok: false }
  } catch (_err) {
    return { ok: false }
  }
}

function normalizeDate(value?: Dayjs | string | null): string | null {
  if (!value) return null
  if (dayjs.isDayjs(value)) return value.toISOString()

  const raw = value.trim()
  if (!raw) return null
  const parsed = dayjs(raw)
  return parsed.isValid() ? parsed.toISOString() : raw
}

function formatDate(value?: string) {
  if (!value) return '—'
  return formatInAppTimezone(value, 'YYYY-MM-DD HH:mm:ss')
}
